import { Fragment, useState } from 'react'

import { Dialog, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  CalendarIcon,
  HomeIcon,
  ListBulletIcon,
  XMarkIcon,
  CalendarDaysIcon,
} from '@heroicons/react/20/solid'
import { CreditCardIcon } from '@heroicons/react/24/solid'
import { SignOut } from '@phosphor-icons/react'
import clsx from 'clsx'
import { hasAccessToPortalFeature } from 'common/patientPortalAuth'
import { formatDisplayName } from 'common/utils'
import {
  GetUnreadMessageTaskCount,
  GetUnreadMessageTaskCountVariables,
} from 'types/graphql'

import { routes, NavLink, useLocation } from '@cedarjs/router'
import { TypedDocumentNode, useQuery } from '@cedarjs/web'
import { Toaster } from '@cedarjs/web/toast'

import { usePatientPortalAuth } from 'src/auth'
import Badge from 'src/components/atoms/Badge/Badge'
import Button from 'src/components/atoms/Button/Button'
import PracticeLogo from 'src/components/atoms/PracticeLogo/PracticeLogo'
import StackView from 'src/components/atoms/StackView/StackView'
import Typography from 'src/components/atoms/Typography/Typography'
import Modal from 'src/components/molecules/Modal'
import { useLogOutAndSyncTabs } from 'src/hooks/useLogOutAndSyncTabs/useLogOutAndSyncTabs'
import { formatPhoneNumber } from 'src/lib/formatters'
import { useCanSelfScheduleVisit } from 'src/pages/PortalPatientSelfScheduling/useCanSelfScheduleVisit'
import { usePatientPortalContext } from 'src/providers/context/PatientPortalContext/PatientPortalContext'

import { SIDEPANEL_PORTAL_ID } from './utils'

const UNREAD_MESSAGE_QUERY: TypedDocumentNode<
  GetUnreadMessageTaskCount,
  GetUnreadMessageTaskCountVariables
> = gql`
  query GetUnreadMessageTaskCount {
    unreadFamilyMessageTaskCount
  }
`

const Sidebar = ({
  closeButton,
  closeMenu,
  isMobileMenu,
}: {
  closeButton?: React.ReactNode
  closeMenu: () => void
  isMobileMenu: boolean
}) => {
  const { currentUser } = usePatientPortalAuth()
  const logOutAndSyncTabs = useLogOutAndSyncTabs()
  const ctx = usePatientPortalContext()!
  const tenant = ctx.tenant!
  const tenantId = tenant.id

  const [modalIsOpen, setModalIsOpen] = useState(false)

  const { data } = useQuery<
    GetUnreadMessageTaskCount,
    GetUnreadMessageTaskCountVariables
  >(UNREAD_MESSAGE_QUERY)

  const linkClasses = [
    'rounded-base-border-radius-selectable-s',
    'text-base-color-fg-default',
    'hover:text-base-color-fg-default',
  ]

  const { canSelfScheduleVisit } = useCanSelfScheduleVisit()

  return (
    <StackView
      className="h-full px-core-space-100 py-core-space-150"
      justifyContent="between"
      testId="sidenav"
    >
      <StackView gap={100}>
        <StackView direction="row" justifyContent="between" alignItems="center">
          <PracticeLogo
            className={
              isMobileMenu ? 'h-core-space-150 w-auto' : 'h-auto w-full'
            }
            logoUrl={tenant.logoUrl ?? `/img/logos/${tenantId}.png`}
          />

          {closeButton}
        </StackView>
        <StackView>
          {[
            {
              id: 'home',
              label: 'Home',
              icon: HomeIcon,
              to: routes.patientPortalHome({
                tenantPortalKey: tenant.portalKey!,
              }),
            },
            {
              id: 'visits',
              label: 'Visits',
              icon: CalendarDaysIcon,
              to: routes.patientPortalVisitsRoot({
                tenantPortalKey: tenant.portalKey!,
              }),
              hide: !hasAccessToPortalFeature({
                user: currentUser!,
                feature: 'UPCOMING_APPOINTMENTS',
              }),
            },
            {
              id: 'medical-records',
              label: 'Records',
              icon: CalendarIcon,
              to: routes.patientPortalMedicalRecordsRoot({
                tenantPortalKey: tenant.portalKey!,
              }),
              hide: !hasAccessToPortalFeature({
                user: currentUser!,
                feature: 'MEDICAL_RECORDS',
              }),
            },
            {
              id: 'messages',
              label: 'Messages',
              icon: ListBulletIcon,
              to: routes.patientPortalMessageList({
                tenantPortalKey: tenant.portalKey!,
              }),
              notification: data?.unreadFamilyMessageTaskCount ? (
                <Badge
                  text={data.unreadFamilyMessageTaskCount}
                  color="purple"
                />
              ) : null,
              hide: !hasAccessToPortalFeature({
                user: currentUser!,
                feature: 'MESSAGES',
              }),
            },
            {
              id: 'payments',
              label: 'Payments',
              icon: CreditCardIcon,
              to: routes.patientPortalPayments({
                tenantPortalKey: tenant.portalKey!,
              }),
              hide: !hasAccessToPortalFeature({
                user: currentUser!,
                feature: 'BILLING',
              }),
            },
            {
              id: 'scheduleVisit',
              label: 'Schedule Visit',
              icon: CalendarDaysIcon,
              to: routes.portalPatientSelfSchedulingRoot({
                tenantPortalKey: tenant.portalKey!,
              }),
              hide:
                !hasAccessToPortalFeature({
                  user: currentUser!,
                  feature: 'SCHEDULE_VISIT',
                }) || !canSelfScheduleVisit,
            },
          ]
            .filter((item) => !item.hide)
            .map(({ id, label, icon: Icon, to, notification }) => {
              return (
                <NavLink
                  to={to}
                  key={id}
                  className={clsx(linkClasses)}
                  activeClassName={clsx([
                    ...linkClasses,
                    'bg-base-color-bg-subtle',
                  ])}
                  matchSubPaths={id === 'messages'}
                  onClick={() => closeMenu()}
                >
                  <StackView
                    direction="row"
                    justifyContent="between"
                    alignItems="center"
                  >
                    <StackView
                      direction="row"
                      alignItems="center"
                      className="p-core-space-50"
                      gap={50}
                    >
                      <Icon className="h-base-size-icon-xs w-base-size-icon-xs" />
                      <Typography
                        textStyle="interface-strong-s"
                        color="text-base-color-fg-muted"
                      >
                        {label}
                      </Typography>
                    </StackView>
                    {notification}
                  </StackView>
                </NavLink>
              )
            })}
        </StackView>
      </StackView>

      <StackView gap={100} alignItems="start">
        <StackView className="border-b-core-border-width-10 border-t-core-border-width-10 border-base-color-bg-subtle py-core-space-100">
          <StackView direction="row">
            <StackView>
              <Typography
                textStyle="interface-strong-xs"
                color="text-base-color-fg-muted"
              >
                {formatDisplayName(currentUser!)}
              </Typography>
              <Typography
                textStyle="interface-default-xs"
                color="text-base-color-fg-subtle"
              >
                {formatPhoneNumber(currentUser!.mobileNumber)}
              </Typography>
            </StackView>

            <Button
              buttonStyle="ghost"
              icon={SignOut}
              tooltip="Log out"
              testId="patient-portal-log-out-btn"
              onClick={() => setModalIsOpen(true)}
            />
          </StackView>
        </StackView>

        <Modal
          title="Are you sure you want to log out?"
          modalStyle="warning"
          primaryButton={{
            text: 'Log out',
            onClick: logOutAndSyncTabs,
          }}
          isOpen={modalIsOpen}
          setIsOpen={setModalIsOpen}
          hideIcon={true}
          testId="patient-portal-logout-modal"
          contentClassName="p-core-space-200 m:p-core-space-150 w-screen sm:w-auto md:max-w-lg"
        />
        <img
          className="h-core-space-150 w-auto"
          src="/img/logo-grayscale.svg"
          alt="Develo"
        />
      </StackView>
    </StackView>
  )
}

export const PatientPortalLayout = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const ctx = usePatientPortalContext()!
  const tenant = ctx.tenant!
  const tenantId = tenant.id
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const shouldHideHamburgerMenuPathnames = [
    routes.portalPatientSelfSchedulingRoot({
      tenantPortalKey: tenant.portalKey!,
    }),
  ]

  const location = useLocation()
  const shouldHideHamburgerMenu = !!shouldHideHamburgerMenuPathnames.find(
    (shouldHideHamburgerMenuPathname) => {
      return location.pathname === shouldHideHamburgerMenuPathname
    }
  )

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <div className="flex h-full">
      <Toaster toastOptions={{ className: 'rw-toast', duration: 6000 }} />

      {/* Sidebar */}
      <section className="hidden w-core-space-1100 border-r-core-border-width-10 border-base-color-bg-subtle md:block">
        <Sidebar closeMenu={closeMobileMenu} isMobileMenu={false} />
      </section>

      {/* Mobile menu */}

      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-20 md:hidden"
          onClose={setMobileMenuOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600/75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative flex w-fit flex-1 flex-col bg-white">
                <Sidebar
                  closeButton={
                    <Button
                      buttonStyle="ghost"
                      onClick={() => setMobileMenuOpen(false)}
                      icon={XMarkIcon}
                      buttonSize="l"
                    />
                  }
                  closeMenu={closeMobileMenu}
                  isMobileMenu
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="w-full md:hidden">
          <StackView
            direction="row"
            className="px-core-space-100 py-core-space-75"
            justifyContent="between"
            alignItems="center"
          >
            <PracticeLogo
              className="h-core-space-150 w-auto"
              logoUrl={tenant.logoUrl ?? `/img/logos/${tenantId}.png`}
            />

            {shouldHideHamburgerMenu ? null : (
              <Button
                buttonStyle="ghost"
                icon={Bars3Icon}
                onClick={() => setMobileMenuOpen(true)}
              />
            )}
          </StackView>
        </header>

        {/* Main content */}
        <main className="flex flex-1 items-stretch justify-center overflow-y-auto">
          {children}
        </main>
      </div>

      <div id={SIDEPANEL_PORTAL_ID} />
    </div>
  )
}
